import { useCallback, useEffect, useRef, useState, type DragEvent, type ReactNode } from "react";
import { useBrand } from "../brandContext";
import { Select } from "../components/Select";

type Asset = {
  id: number;
  kind: string;
  filename: string;
  phash: string;
};

type SearchResult = {
  filename?: string;
  matches: {
    assetId: number;
    kind: string;
    filename: string;
    distance: number;
    similarity: number;
    matched: boolean;
    fileUrl: string;
  }[];
  best: { similarity: number; matched: boolean; filename: string } | null;
  reverseSearch: {
    yandex: string;
    google: string;
    bing: string;
    note: string;
  };
  threshold: number;
};

type DropTarget = "asset" | "check";

function isImageLike(file: File): boolean {
  if (/^image\//i.test(file.type)) return true;
  return /\.(png|jpe?g|webp|gif|bmp)$/i.test(file.name);
}

function isZipLike(file: File): boolean {
  if (/\.zip$/i.test(file.name)) return true;
  return /zip/i.test(file.type || "");
}

function isUploadLike(file: File): boolean {
  return isImageLike(file) || isZipLike(file);
}

function fileFromClipboard(items: DataTransferItemList | null): File | null {
  if (!items) return null;
  for (const item of items) {
    if (item.kind === "file" && item.type.startsWith("image/")) {
      return item.getAsFile();
    }
  }
  return null;
}

/** Server accepts png/jpeg — convert others via canvas. Zip left as-is. */
async function toPngOrJpeg(file: File): Promise<File> {
  if (isZipLike(file)) return file;
  if (/^image\/(png|jpeg)$/i.test(file.type)) return file;
  if (file.type === "image/jpg") {
    return new File([file], file.name.replace(/\.jpg$/i, ".jpeg") || "paste.jpeg", {
      type: "image/jpeg",
    });
  }
  const bmp = await createImageBitmap(file);
  const canvas = document.createElement("canvas");
  canvas.width = bmp.width;
  canvas.height = bmp.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas unavailable");
  ctx.drawImage(bmp, 0, 0);
  bmp.close();
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
      "image/png",
    );
  });
  const base = (file.name || "paste").replace(/\.[^.]+$/, "") || "paste";
  return new File([blob], `${base}.png`, { type: "image/png" });
}

function DropZone({
  target,
  active,
  busy,
  label,
  onPick,
  children,
}: {
  target: DropTarget;
  active: boolean;
  busy: boolean;
  label: string;
  onPick: (files: FileList | File[]) => void;
  children?: ReactNode;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const dragDepth = useRef(0);

  function onDragEnter(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current += 1;
    setOver(true);
  }
  function onDragLeave(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setOver(false);
  }
  function onDragOver(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  }
  function onDrop(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = 0;
    setOver(false);
    const files = [...(e.dataTransfer.files || [])].filter(isUploadLike);
    if (files.length) onPick(files);
  }

  return (
    <div
      className={`drop-zone${over ? " is-over" : ""}${active ? " is-paste-target" : ""}${busy ? " is-busy" : ""}`}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
      data-drop-target={target}
    >
      <div className="drop-zone-body">
        <p className="drop-zone-title">{label}</p>
        <p className="hint drop-zone-hint">
          Несколько файлов или .zip · Ctrl+V · выбрать
        </p>
        <button
          type="button"
          className="btn primary"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
        >
          Выбрать файлы / ZIP
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif,.zip,application/zip"
          hidden
          multiple
          onChange={(e) => {
            if (e.target.files?.length) onPick(e.target.files);
            e.target.value = "";
          }}
        />
      </div>
      {children}
    </div>
  );
}

export function ImagesPage() {
  const { activeId, brands } = useBrand();
  const brand = brands.find((b) => b.id === activeId);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [kind, setKind] = useState("logo");
  const [result, setResult] = useState<SearchResult | null>(null);
  const [batchResults, setBatchResults] = useState<SearchResult[] | null>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [checkUrl, setCheckUrl] = useState("");
  const [pasteTarget, setPasteTarget] = useState<DropTarget>("asset");
  const [importText, setImportText] = useState("");
  const [importHint, setImportHint] = useState("");
  const [searchAfterUpload, setSearchAfterUpload] = useState(true);
  const [batchLinks, setBatchLinks] = useState<
    { assetId: number; filename: string; yandex: string; bing: string; google: string }[]
  >([]);
  const kindRef = useRef(kind);
  const pasteTargetRef = useRef(pasteTarget);
  const busyRef = useRef(busy);

  kindRef.current = kind;
  pasteTargetRef.current = pasteTarget;
  busyRef.current = busy;

  async function reload(): Promise<Asset[]> {
    if (!activeId) return [];
    const d = await fetch(`/api/brands/${activeId}/assets`).then((r) => r.json());
    const items = (d.items || []) as Asset[];
    setAssets(items);
    return items;
  }

  useEffect(() => {
    void reload();
  }, [activeId]);

  const uploadBatch = useCallback(
    async (rawFiles: File[]) => {
      if (!activeId) return [] as Asset[];
      const fd = new FormData();
      fd.append("kind", kindRef.current);
      for (const raw of rawFiles) {
        const file = await toPngOrJpeg(raw);
        fd.append("file", file);
      }
      setProgress(`Загрузка ${rawFiles.length} файл(ов)…`);
      const r = await fetch(`/api/brands/${activeId}/assets`, {
        method: "POST",
        body: fd,
      });
      const d = (await r.json()) as {
        error?: string;
        asset?: Asset;
        assets?: Asset[];
        uploaded?: number;
      };
      if (!r.ok) throw new Error(d.error || "upload failed");
      return d.assets || (d.asset ? [d.asset] : []);
    },
    [activeId],
  );

  const runCheckBatch = useCallback(
    async (rawFiles: File[]) => {
      if (!activeId) return;
      const fd = new FormData();
      for (const raw of rawFiles) {
        const file = await toPngOrJpeg(raw);
        fd.append("file", file);
      }
      setProgress(`Проверка ${rawFiles.length} изображений…`);
      const r = await fetch(`/api/image-search?brandId=${activeId}`, {
        method: "POST",
        body: fd,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "search failed");
      if (d.batch && Array.isArray(d.items)) {
        setResult(null);
        setBatchResults(d.items as SearchResult[]);
      } else {
        setBatchResults(null);
        setResult(d as SearchResult);
      }
    },
    [activeId],
  );

  const runCheckUrl = useCallback(async () => {
    if (!activeId || !checkUrl.trim()) return;
    setErr("");
    setBusy(true);
    setResult(null);
    setBatchResults(null);
    setProgress("");
    try {
      const fd = new FormData();
      fd.append("url", checkUrl.trim());
      const r = await fetch(`/api/image-search?brandId=${activeId}`, {
        method: "POST",
        body: fd,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "search failed");
      setResult(d);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setProgress("");
    }
  }, [activeId, checkUrl]);

  const openYandexLimited = useCallback(
    (links: { yandex: string }[], max = 5) => {
      for (const l of links.slice(0, max)) {
        window.open(l.yandex, "_blank", "noopener,noreferrer");
      }
    },
    [],
  );

  const runReverseSearch = useCallback(
    async (assetId: number) => {
      if (!activeId) return;
      setErr("");
      setBusy(true);
      setProgress("Обратный поиск…");
      try {
        const r = await fetch(
          `/api/brands/${activeId}/assets/${assetId}/reverse-search`,
          { method: "POST" },
        );
        const d = (await r.json()) as {
          error?: string;
          blocked?: boolean;
          hint?: string;
          fetched?: number;
          inserted?: number;
          updated?: number;
          links?: { yandex?: string; bing?: string; google?: string };
        };
        if (!r.ok) {
          if (d.blocked || /captcha|403|429|blocked/i.test(d.error || "")) {
            if (d.links?.yandex) {
              window.open(d.links.yandex, "_blank", "noopener,noreferrer");
            }
            setImportHint(
              d.hint ||
                "Автопоиск с сервера заблокирован капчей. В браузере открыт Яндекс — скопируйте ссылки и вставьте ниже.",
            );
            throw new Error(d.error || "captcha");
          }
          throw new Error(d.error || "reverse search failed");
        }
        setImportHint("");
        window.alert(
          `Найдено в интернете: ${d.fetched || 0} страниц` +
            (d.inserted ? `, новых ${d.inserted}` : "") +
            (d.updated ? `, обновлено ${d.updated}` : "") +
            "\nСмотрите ленту → источник «Похожие фото».",
        );
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
        setProgress("");
      }
    },
    [activeId],
  );

  const runReverseSearchAll = useCallback(
    async (assetIds?: number[]) => {
      if (!activeId) return;
      setErr("");
      setBusy(true);
      setProgress("Обратный поиск по всем эталонам…");
      try {
        const r = await fetch(
          `/api/brands/${activeId}/assets/reverse-search-batch`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(assetIds?.length ? { assetIds } : {}),
          },
        );
        const d = (await r.json()) as {
          error?: string;
          hint?: string;
          ok?: number;
          blocked?: number;
          failed?: number;
          fetched?: number;
          inserted?: number;
          updated?: number;
          items?: {
            assetId: number;
            filename: string;
            ok: boolean;
            blocked?: boolean;
            links: { yandex: string; bing: string; google: string };
          }[];
        };
        if (d.error && !d.items?.length) throw new Error(d.error);

        const blockedLinks = (d.items || [])
          .filter((i) => i.blocked || !i.ok)
          .map((i) => ({
            assetId: i.assetId,
            filename: i.filename,
            yandex: i.links.yandex,
            bing: i.links.bing,
            google: i.links.google,
          }));
        setBatchLinks(blockedLinks.length ? blockedLinks : (d.items || []).map((i) => ({
          assetId: i.assetId,
          filename: i.filename,
          yandex: i.links.yandex,
          bing: i.links.bing,
          google: i.links.google,
        })));

        if (d.blocked && d.blocked > 0) {
          openYandexLimited(blockedLinks, 5);
          setImportHint(
            d.hint ||
              `Капча на ${d.blocked} изображении(ях). Открыл до 5 вкладок Яндекса; остальные ссылки ниже.`,
          );
        } else {
          setImportHint("");
        }

        window.alert(
          `Поиск по ${(d.items || []).length} эталонам\n` +
            `ok: ${d.ok || 0}, капча: ${d.blocked || 0}, ошибки: ${d.failed || 0}\n` +
            `страниц: ${d.fetched || 0}` +
            (d.inserted ? `, новых ${d.inserted}` : "") +
            (d.updated ? `, обновлено ${d.updated}` : "") +
            "\nЛента → «Похожие фото».",
        );
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
        setProgress("");
      }
    },
    [activeId, openYandexLimited],
  );

  const importUrls = useCallback(async () => {
    if (!activeId || !importText.trim()) return;
    setErr("");
    setBusy(true);
    try {
      const urls = importText
        .split(/[\s,;]+/)
        .map((u) => u.trim())
        .filter((u) => /^https:\/\//i.test(u));
      if (!urls.length) throw new Error("Нужны https:// ссылки (по одной в строке или через пробел)");
      const r = await fetch(`/api/brands/${activeId}/import-image-urls`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ urls, assetLabel: "reverse" }),
      });
      const d = (await r.json()) as {
        error?: string;
        fetched?: number;
        inserted?: number;
        updated?: number;
      };
      if (!r.ok) throw new Error(d.error || "import failed");
      setImportText("");
      setImportHint("");
      window.alert(
        `Импорт: ${d.fetched || 0} URL` +
          (d.inserted ? `, новых ${d.inserted}` : "") +
          (d.updated ? `, обновлено ${d.updated}` : "") +
          "\nЛента → «Похожие фото».",
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [activeId, importText]);

  const handleFiles = useCallback(
    async (target: DropTarget, list: FileList | File[]) => {
      const files = [...list].filter(isUploadLike);
      if (!files.length) {
        setErr("Нужны изображения (png/jpeg/webp) или ZIP-архив");
        return;
      }
      setPasteTarget(target);
      setErr("");
      setBusy(true);
      setProgress("");
      try {
        if (target === "asset") {
          const uploaded = await uploadBatch(files);
          const items = await reload();
          setProgress(`Загружено ${uploaded.length || files.length}`);
          if (searchAfterUpload) {
            const ids = uploaded.map((a) => a.id).filter(Boolean);
            await runReverseSearchAll(ids.length ? ids : undefined);
          }
          if (!items.length && !uploaded.length) {
            /* noop */
          }
        } else {
          setResult(null);
          setBatchResults(null);
          await runCheckBatch(files);
        }
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
        setProgress("");
      }
    },
    [uploadBatch, runCheckBatch, runReverseSearchAll, searchAfterUpload],
  );

  const handleFilesRef = useRef(handleFiles);
  handleFilesRef.current = handleFiles;

  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement)?.isContentEditable) {
        return;
      }
      if (busyRef.current) return;
      const file = fileFromClipboard(e.clipboardData?.items ?? null);
      if (!file) return;
      e.preventDefault();
      void handleFilesRef.current(pasteTargetRef.current, [file]);
    }
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []);

  if (!activeId || !brand) {
    return <div className="empty">Сначала создайте и выберите бренд.</div>;
  }

  function renderSingleResult(r: SearchResult, key?: string) {
    return (
      <div key={key} style={{ marginTop: 16 }}>
        {r.filename ? (
          <p>
            <strong>{r.filename}</strong>
          </p>
        ) : null}
        <p>
          Лучшее совпадение с эталоном:{" "}
          <strong>
            {r.best
              ? `${r.best.similarity}% (${r.best.filename})`
              : "нет эталонов"}
          </strong>
          {r.best?.matched ? (
            <span className="tag ok"> похоже</span>
          ) : r.best ? (
            <span className="tag warn"> слабо</span>
          ) : null}
        </p>
        <p className="hint">Порог dHash ≤ {r.threshold}</p>
        <div className="row-actions" style={{ marginTop: 12 }}>
          <a className="btn" href={r.reverseSearch.yandex} target="_blank" rel="noreferrer">
            Яндекс.Картинки
          </a>
          <a className="btn" href={r.reverseSearch.google} target="_blank" rel="noreferrer">
            Google Lens
          </a>
          <a className="btn" href={r.reverseSearch.bing} target="_blank" rel="noreferrer">
            Bing
          </a>
        </div>
        <ul className="match-list">
          {r.matches.map((m) => (
            <li key={m.assetId}>
              {m.filename}: {m.similarity}% (dist {m.distance})
              {m.matched ? " · match" : ""}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <>
      <div className="page-head">
        <h2>Логотип и картинки</h2>
      </div>
      <p className="hint" style={{ marginTop: 0 }}>
        Эталоны привязаны к активному бренду «{brand.name}» (id {activeId}).
        Можно загрузить несколько картинок или ZIP — поиск по всем сразу.
        {pasteTarget === "asset" ? " Зона: эталоны." : " Зона: проверка."}
      </p>
      {err ? <p className="error">{err}</p> : null}
      {progress ? <p className="hint">{progress}</p> : null}
      <div className="grid-2">
        <div
          className="panel"
          onMouseEnter={() => setPasteTarget("asset")}
          onFocusCapture={() => setPasteTarget("asset")}
        >
          <h3>Эталоны «{brand.name}»</h3>
          <p className="hint">
            Логотипы и фото карточек. Локальный dHash + обратный поиск в
            интернете по всем загруженным.
          </p>
          <div className="row-actions">
            <div style={{ minWidth: 180 }}>
              <Select
                value={kind}
                onChange={setKind}
                options={[
                  { value: "logo", label: "Логотип" },
                  { value: "card", label: "Фото карточки" },
                  { value: "pack", label: "Упаковка" },
                ]}
              />
            </div>
            <label className="hint" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={searchAfterUpload}
                onChange={(e) => setSearchAfterUpload(e.target.checked)}
                disabled={busy}
              />
              Сразу искать в интернете
            </label>
            <button
              className="btn primary"
              type="button"
              disabled={busy || !assets.length}
              onClick={() => void runReverseSearchAll()}
              title="Обратный поиск по всем эталонам"
            >
              Искать все ({assets.length})
            </button>
          </div>
          <DropZone
            target="asset"
            active={pasteTarget === "asset"}
            busy={busy}
            label="Бросьте картинки или ZIP"
            onPick={(files) => void handleFiles("asset", files)}
          />
          <div className="asset-grid">
            {assets.map((a) => (
              <div className="asset-card" key={a.id}>
                <img
                  src={`/api/brands/${activeId}/assets/${a.id}/file`}
                  alt={a.filename}
                />
                <div>
                  <strong>{a.kind}</strong>
                  <div className="hint">{a.filename}</div>
                </div>
                <div className="asset-card-actions">
                  <button
                    className="btn primary"
                    type="button"
                    disabled={busy}
                    title="Обратный поиск этого изображения"
                    onClick={() => void runReverseSearch(a.id)}
                  >
                    Искать в интернете
                  </button>
                  <button
                    className="btn"
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      void fetch(`/api/brands/${activeId}/assets/${a.id}`, {
                        method: "DELETE",
                      }).then(() => reload());
                    }}
                  >
                    Удалить
                  </button>
                </div>
              </div>
            ))}
            {!assets.length ? <div className="empty">Эталонов пока нет</div> : null}
          </div>
          {batchLinks.length ? (
            <div style={{ marginTop: 16 }}>
              <h3 style={{ marginBottom: 8 }}>Ссылки обратного поиска</h3>
              <p className="hint">
                При капче откройте вручную (браузер открыл до 5 вкладок).
              </p>
              <ul className="match-list">
                {batchLinks.map((l) => (
                  <li key={l.assetId}>
                    {l.filename}:{" "}
                    <a href={l.yandex} target="_blank" rel="noreferrer">
                      Яндекс
                    </a>
                    {" · "}
                    <a href={l.bing} target="_blank" rel="noreferrer">
                      Bing
                    </a>
                    {" · "}
                    <a href={l.google} target="_blank" rel="noreferrer">
                      Lens
                    </a>
                  </li>
                ))}
              </ul>
              {batchLinks.length > 5 ? (
                <button
                  className="btn"
                  type="button"
                  disabled={busy}
                  onClick={() => openYandexLimited(batchLinks.slice(5), 10)}
                >
                  Открыть ещё Яндекс (до 10)
                </button>
              ) : null}
            </div>
          ) : null}
          <div style={{ marginTop: 20 }}>
            <h3 style={{ marginBottom: 8 }}>Импорт ссылок из поиска</h3>
            <p className="hint">
              При капче скопируйте URL страниц из Яндекса/Bing и вставьте сюда —
              попадут в ленту как «Похожие фото».
            </p>
            {importHint ? (
              <p className="hint" style={{ color: "var(--warn, #b45309)" }}>
                {importHint}
              </p>
            ) : null}
            <textarea
              className="input"
              rows={5}
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              placeholder={"https://...\nhttps://..."}
              disabled={busy}
              style={{ width: "100%", fontFamily: "inherit", resize: "vertical" }}
            />
            <div className="row-actions" style={{ marginTop: 8 }}>
              <button
                className="btn primary"
                type="button"
                disabled={busy || !importText.trim()}
                onClick={() => void importUrls()}
              >
                Добавить в ленту
              </button>
            </div>
          </div>
        </div>
        <div
          className="panel"
          onMouseEnter={() => setPasteTarget("check")}
          onFocusCapture={() => setPasteTarget("check")}
        >
          <h3>Проверка против «{brand.name}»</h3>
          <p className="hint">
            Несколько файлов или ZIP — dHash сразу по всем против эталонов (
            {assets.length} шт.).
          </p>
          <DropZone
            target="check"
            active={pasteTarget === "check"}
            busy={busy}
            label="Бросьте картинки или ZIP для проверки"
            onPick={(files) => void handleFiles("check", files)}
          />
          <div className="url-check-row">
            <input
              className="input feed-search"
              value={checkUrl}
              onChange={(e) => setCheckUrl(e.target.value)}
              placeholder="или https://… URL картинки карточки"
              disabled={busy}
            />
            <button
              className="btn primary"
              type="button"
              disabled={busy || !checkUrl.trim()}
              onClick={() => void runCheckUrl()}
            >
              Проверить URL
            </button>
          </div>
          {batchResults ? (
            <div style={{ marginTop: 12 }}>
              <p className="hint">Проверено: {batchResults.length}</p>
              <div className="row-actions" style={{ marginBottom: 8 }}>
                <button
                  className="btn"
                  type="button"
                  onClick={() =>
                    openYandexLimited(
                      batchResults.map((r) => ({ yandex: r.reverseSearch.yandex })),
                      5,
                    )
                  }
                >
                  Открыть Яндекс по всем (до 5)
                </button>
              </div>
              {batchResults.map((r, i) =>
                renderSingleResult(r, r.filename || String(i)),
              )}
            </div>
          ) : result ? (
            renderSingleResult(result)
          ) : null}
        </div>
      </div>
    </>
  );
}
